/**
 * The look of a text field on a sign-in form.
 *
 * Kept as class strings rather than a component because both sign-in forms
 * render a bare `<input>` — they need `type`, `autoComplete`, `aria-invalid`
 * and a reveal button layered on top, and wrapping that in a component would
 * only pass everything straight through.
 *
 * Shared because the console's fields were 44px with a 6px radius while the
 * product's were 48px with a 16px one, which is the sort of difference nobody
 * can name and everybody notices.
 */
export const FIELD =
  'h-control-lg w-full rounded-lg border border-border bg-card px-3 text-base text-foreground outline-none ' +
  'transition-[border-color,box-shadow] duration-150 placeholder:text-muted-foreground ' +
  'focus:border-ring focus:ring-2 focus:ring-ring/25';

/** Applied on top of FIELD once a field has been judged and found wanting. */
export const FIELD_INVALID =
  'border-destructive focus:border-destructive focus:ring-destructive/25';

/**
 * The look of a control inside the app: the same frame `Input` draws, for the
 * native `<select>` and `<textarea>` elements a form still renders bare. Those
 * had drifted to their own copy — py-2 instead of the control height, an accent
 * focus ring instead of the ring token — so a select sat a few pixels off the
 * input beside it and lit up in a different colour.
 */
export const CONTROL =
  'w-full rounded-lg border border-border bg-card px-3 text-base text-foreground outline-none ' +
  'transition-[color,background-color,border-color,box-shadow] duration-150 ' +
  'placeholder:text-muted-foreground ' +
  'focus-visible:border-ring focus-visible:ring-2 focus-visible:ring-ring/25 ' +
  'disabled:cursor-not-allowed disabled:opacity-50';

/** A native `<select>` at the height of every other control. */
export const SELECT_CONTROL = `${CONTROL} h-control`;

/** A native `<textarea>`: the control frame, grown by its rows. */
export const TEXTAREA_CONTROL = `${CONTROL} resize-none py-2`;
